/**
 * UnknownRenderer — graceful fallback for content kinds the client doesn't
 * recognise. The shared `parseContentItem` lands unknown types here so a
 * backend-shipping-ahead-of-clients scenario doesn't crash the bubble.
 *
 * Renders the `text` field if present (so a Markdown-emitting future kind
 * still shows its body), otherwise nothing. In dev we surface a one-line
 * note so a developer notices missing client coverage; in prod it's
 * silent — graceful degradation > shouting.
 */
import React from 'react';
import type { Renderer } from '../registry';

// Vite exposes `import.meta.env.DEV` (boolean) — true during `vite dev`,
// false in production builds. Equivalent to NODE_ENV !== 'production'
// without pulling in @types/node.
const isDev = Boolean(import.meta.env?.DEV);

const UnknownRenderer: Renderer = ({ item }) => {
  const fallbackText =
    typeof (item as { text?: string }).text === 'string'
      ? (item as { text?: string }).text
      : null;
  if (fallbackText) {
    return <span className="wg-chat__text">{fallbackText}</span>;
  }
  if (isDev) {
    return (
      <span
        className="wg-chat__unknown"
        title={`Unsupported content type: ${item.type}`}
      >
        [{item.type}]
      </span>
    );
  }
  return null;
};

export default UnknownRenderer;
